
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import StackedBarChartFilterable from '../../filterables/StackedBarChartFilterable.vue';
import DateFilter from '@/components/reports-v3/components/filters/DateFilter.vue';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { getTableName } from '@/worker/db/sqlHelpers';
import gdbx from '@/store/modules/gdbx';
import * as echarts from 'echarts';
import moment from 'moment';
import { GobiColor } from '@/helpers/color';
import { TooltipFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tooltipFormatter';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
	components: {
		StackedBarChartFilterable,
		DateFilter,
		SelectFilter,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class PurchasePeriodComparisonLineChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['purchases'];
	}

	public filterIds: Array<
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
		| 'compareDate1'
		| 'compareDate2'
		| 'comparisonPeriod'
		| 'purchases'
	> = ['purchases', 'compareDate1', 'compareDate2', 'comparisonPeriod'];

	public xAxisData: number[] = [];
	public series: echarts.ECharts[] | any = [];
	public selectedDate1 = moment().startOf('month').add(-1, 'month').valueOf();
	public selectedDate2 = moment()
		.startOf('month')
		.add(-1, 'month')
		.add(-1, 'year')
		.valueOf();
	public period: '31 days' | '12 months' | '24 months' = '31 days';
	public periodOptions = ['31 days', '12 months', '24 months'];

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get selectedDateRange1(): [number, number] {
		const period = this.period || '1 month';
		const periodArgument = period.split(' ');
		return [
			moment(this.selectedDate1).startOf('day').valueOf(),
			moment(this.selectedDate1)
				.add(...periodArgument)
				.endOf('day')
				.valueOf(),
		];
	}

	public get selectedDateRange2(): [number, number] {
		const period = this.period || '1 month';
		const periodArgument = period.split(' ');
		return [
			moment(this.selectedDate2).startOf('day').valueOf(),
			moment(this.selectedDate2)
				.add(...periodArgument)
				.endOf('day')
				.valueOf(),
		];
	}

	public get periodArgument(): [number, 'days' | 'months'] {
		const period = this.period || '1 month';
		const pa = period.split(' ');
		return [parseInt(pa[0], 10), pa[1] as 'days' | 'months'];
	}

	public get xAxisLabel() {
		return this.periodArgument[1] === 'months' ? 'Month' : 'Day';
	}

	public get formatter() {
		const pa = this.periodArgument;
		return pa[1] === 'days'
			? TooltipFormatter.comparisonDay
			: TooltipFormatter.comparisonMonth;
	}

	public get expensiveHook() {
		const {
			periodArgument,
			selectedDateRange1,
			selectedDateRange2,
			selectedAgents,
			selectedSuppliers,
			includeDebitNote,
			includeCreditNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			periodArgument,
			selectedDateRange1,
			selectedDateRange2,
			selectedAgents,
			selectedSuppliers,
			includeDebitNote,
			includeCreditNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		const periodType = this.periodArgument[1] === 'days' ? 'day' : 'month';
		const n = this.periodArgument[0];
		const msInDay = 24 * 60 * 60 * 1000;

		const queryHead = await this._ensureTablesLoaded(
			['purchases', 'purchaseReturn', 'supplierTrades'] as any[],
		);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const purchasesTable = getTableName('purchases');
		const returnTable = getTableName('purchaseReturn');
		const tradesTable = getTableName('supplierTrades');

		let supplierFilter = '';
		if (this.selectedSuppliers && this.selectedSuppliers.length > 0) {
			const list = this.selectedSuppliers.map((s: string) => `'${escapeStr(s)}'`).join(',');
			supplierFilter = ` AND "CODE" IN (${list})`;
		}
		let agentFilter = '';
		if (this.selectedAgents && this.selectedAgents.length > 0) {
			const list = this.selectedAgents.map((a: string) => `'${escapeStr(a)}'`).join(',');
			agentFilter = ` AND "AGENT" IN (${list})`;
		}

		// Build period index SQL expression
		const buildPeriodExpr = (dateRange: [number, number]): string => {
			if (periodType === 'day') {
				return `CAST(FLOOR((TRY_CAST("DOCDATE" AS BIGINT) - ${dateRange[0]}) / ${msInDay}) AS INTEGER) + 1`;
			} else {
				const monthStarts: number[] = [];
				for (let i = 0; i <= n; i++) {
					monthStarts.push(moment(dateRange[0]).startOf('month').add(i, 'month').valueOf());
				}
				const cases = Array.from({ length: n }, (unused, i) =>
					`WHEN TRY_CAST("DOCDATE" AS BIGINT) >= ${monthStarts[i]} AND TRY_CAST("DOCDATE" AS BIGINT) < ${monthStarts[i + 1]} THEN ${i + 1}`,
				).join(' ');
				return `CASE ${cases} END`;
			}
		};

		const buildDateFilter = (dateRange: [number, number]): string => {
			return `TRY_CAST("DOCDATE" AS BIGINT) >= ${dateRange[0]} AND TRY_CAST("DOCDATE" AS BIGINT) <= ${dateRange[1]}`;
		};

		// Helper: query a table grouped by period, return Map<periodIdx, amount>
		const queryByPeriod = async (
			table: string, dateRange: [number, number], docTypeFilter: string, amountCol: string,
		): Promise<number[]> => {
			const periodExpr = buildPeriodExpr(dateRange);
			const dateFilter = buildDateFilter(dateRange);
			const rows = await queryHead.rawQuery(`
				SELECT ${periodExpr} as pi,
					SUM(TRY_CAST(${amountCol} AS DOUBLE)) as total
				FROM ${table}
				WHERE ${dateFilter}${docTypeFilter}${supplierFilter}${agentFilter}
				GROUP BY pi
				ORDER BY pi
			`);
			const arr = new Array(n).fill(0);
			for (const row of rows) {
				const idx = Number(row.pi);
				if (idx >= 1 && idx <= n) {
					arr[idx - 1] = Number(row.total) || 0;
				}
			}
			return arr;
		};

		let data1: number[] = [];
		let data2: number[] = [];

		if (this.slPhModuleOnly) {
			// PH Mode - use purchases + purchaseReturn tables
			const phDocTypes = this.includeDebitNote ? ['PI', 'CP', 'SD'] : ['PI', 'CP'];
			const dtFilter = ` AND "DOCTYPE" IN (${phDocTypes.map((t) => `'${t}'`).join(',')})`;

			const [purchase1, cn1, purchase2, cn2] = await Promise.all([
				queryByPeriod(purchasesTable, this.selectedDateRange1, dtFilter, '"AMOUNT"'),
				queryByPeriod(returnTable, this.selectedDateRange1, '', '"AMOUNT"'),
				queryByPeriod(purchasesTable, this.selectedDateRange2, dtFilter, '"AMOUNT"'),
				queryByPeriod(returnTable, this.selectedDateRange2, '', '"AMOUNT"'),
			]);
			this.checkInterrupted(jobId);

			data1 = purchase1.map((v, i) => v - (this.includeCreditNote ? cn1[i] : 0));
			data2 = purchase2.map((v, i) => v - (this.includeCreditNote ? cn2[i] : 0));
		} else {
			// AP Mode - single query per range with CASE WHEN for doc types
			const queryAPByPeriod = async (dateRange: [number, number]) => {
				const periodExpr = buildPeriodExpr(dateRange);
				const dateFilter = buildDateFilter(dateRange);
				const rows = await queryHead.rawQuery(`
					SELECT ${periodExpr} as pi,
						SUM(CASE WHEN "DOCTYPE" = 'PI' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as pi_amt,
						SUM(CASE WHEN "DOCTYPE" = 'SD' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as sd_amt,
						SUM(CASE WHEN "DOCTYPE" = 'SC' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as sc_amt
					FROM ${tradesTable}
					WHERE ${dateFilter}${supplierFilter}${agentFilter}
						AND "DOCTYPE" IN ('PI', 'SD', 'SC')
					GROUP BY pi
					ORDER BY pi
				`);
				const piArr = new Array(n).fill(0);
				const sdArr = new Array(n).fill(0);
				const scArr = new Array(n).fill(0);
				for (const row of rows) {
					const idx = Number(row.pi);
					if (idx >= 1 && idx <= n) {
						piArr[idx - 1] = Number(row.pi_amt) || 0;
						sdArr[idx - 1] = Number(row.sd_amt) || 0;
						scArr[idx - 1] = Number(row.sc_amt) || 0;
					}
				}
				return { pi: piArr, sd: sdArr, sc: scArr };
			};

			const [ap1, ap2] = await Promise.all([
				queryAPByPeriod(this.selectedDateRange1),
				queryAPByPeriod(this.selectedDateRange2),
			]);
			this.checkInterrupted(jobId);

			data1 = ap1.pi.map((v, i) =>
				v + (this.includeDebitNote ? ap1.sd[i] : 0)
				- (this.includeCreditNote ? ap1.sc[i] : 0),
			);
			data2 = ap2.pi.map((v, i) =>
				v + (this.includeDebitNote ? ap2.sd[i] : 0)
				- (this.includeCreditNote ? ap2.sc[i] : 0),
			);
		}

		this.generateLoadingText(1);

		this.xAxisData = [...Array(n + 1).keys()].slice(1);

		const slicedData1 = data1.slice(0, n);
		const slicedData2 = data2.slice(0, n);

		const result = [
			{
				name: moment(this.selectedDate1).format('DD MMM YYYY'),
				type: 'bar',
				smooth: true,
				barWidth: '40%',
				color: GobiColor.COLOR1,
				data: slicedData1,
			},
			{
				name: moment(this.selectedDate2).format('DD MMM YYYY'),
				type: 'bar',
				smooth: true,
				barWidth: '40%',
				color: GobiColor.COLOR3,
				data: slicedData2,
			},
		];

		this.series = result;

		this.saveHistory('series', 'xAxisData');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
